import { DateTime, Duration } from 'luxon'

export class Time {
  static formatAxisLabelForEchartsWhenTimeZoneDoesNotMatch(timeZone, value) {
    const DATE_ONLY = 'MMM d'
    const DATE_TO_MINUTES = 'MMM d\nHH:mm'
    const DATE_TO_SECONDS = 'MMM d\nHH:mm:ss'
    const YEAR_ONLY = 'yyyy'
    const YEAR_TO_MINUTES = 'yyyy\nHH:mm'
    const YEAR_TO_SECONDS = 'yyyy\nHH:mm:ss'

    let convertedDate = DateTime.fromMillis(value, { zone: timeZone })

    if (convertedDate.month == 1 && convertedDate.day == 1) {
      if (convertedDate.second) {
        return convertedDate.toFormat(YEAR_TO_SECONDS)
      }
      if (convertedDate.hour || convertedDate.minute) {
        return convertedDate.toFormat(YEAR_TO_MINUTES)
      }
      return convertedDate.toFormat(YEAR_ONLY)
    } else {
      if (convertedDate.second) {
        return convertedDate.toFormat(DATE_TO_SECONDS)
      }
      if (convertedDate.hour || convertedDate.minute) {
        return convertedDate.toFormat(DATE_TO_MINUTES)
      }
      return convertedDate.toFormat(DATE_ONLY)
    }
  }

  static formatSliderLabelWhenTimeZoneDoesNotMatch(timeZone, value) {
    let time = DateTime.fromMillis(value, { zone: timeZone })
    return time.toFormat('yyyy-MM-dd\nh:mm:ss a')
  }

  static nowDate() {
    return DateTime.now()
      .set({ hour: 0, minute: 0, second: 0, milliseconds: 0 })
      .toISO({ suppressMilliseconds: true, includeOffset: false })
  }

  static simpleDurationFromDates(start, end) {
    const startDate = DateTime.fromISO(start, { zone: 'utc' }).set({ milliseconds: 0 })
    const endDate = DateTime.fromISO(end, { zone: 'utc' }).set({ milliseconds: 0 })
    let duration = endDate.diff(startDate, ['years', 'months', 'days', 'hours']).normalize()
    const d = duration.toObject()

    let finalOpts = []

    if (d.years) {
      finalOpts.push('years')
    }
    if (d.months) {
      finalOpts.push('months')
    }
    if (d.months) {
      finalOpts.push('days')
    }
    if (d.hours) {
      finalOpts.push('hours')
    }

    if (finalOpts.length > 1) {
      //  If the diff resulted in more than one component, reduce it down to the
      //  most specific component and run the diff again so we only have 1.
      finalOpts = [finalOpts.pop()]
      duration = endDate.diff(startDate, finalOpts).normalize()
    }
    return duration.toISO()
  }

  static toHuman(duration, options = {}) {
    options = { maximumFractionDigits: 2, ...options }
    if (typeof duration === 'string' && duration) {
      return Duration.fromISO(duration).toHuman(options)
    } else if (duration instanceof Duration) {
      return duration.toHuman(options)
    }
    return ''
  }

  static toRelative(from, options = {}) {
    if (!from) return null
    const fromTime = typeof from === 'string' ? DateTime.fromISO(from, options) : from
    const currentTime = options.zone ? DateTime.now().setZone(options.zone) : DateTime.now()
    const diff = currentTime.diff(fromTime, 'minutes').minutes
    if (Math.abs(diff) < 1) {
      return `Just now`
    }
    return fromTime.toRelative()
  }

  static getDurationUnits(isoDuration) {
    const durationUnitProperties = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds']
    const duration = Duration.fromISO(isoDuration)
    let units = []
    durationUnitProperties.forEach(unitProperty => {
      if (duration.values && Object.hasOwn(duration.values, unitProperty)) {
        units.push(unitProperty)
      }
    })
    return units
  }

  //  Looks for any parts of the duration that are decimal and truncates them
  //  to integers.
  static truncateDuration(isoDuration) {
    let durationObject = Duration.fromISO(isoDuration).toObject()
    for (let part in durationObject) {
      durationObject[part] = Math.trunc(durationObject[part])
    }
    return Duration.fromObject(durationObject).toISO()
  }

  static cleanMilliseconds(utcDateString) {
    return !utcDateString
      ? null
      : DateTime.fromISO(utcDateString, { zone: 'utc' })
          .toUTC()
          .set({ milliseconds: 0 })
          .toISO({ suppressMilliseconds: true })
  }
}

export default { Time }
